import { secureUnhash } from '../utils/patientIdCipher';

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const emailKey = value => typeof value === 'string' ? value.trim().toLowerCase() : '';
const digits = value => String(value || '').replace(/\D/g, '');
const timestamp = value => value ? new Date(value).getTime() : NaN;

// Match Calendar's completed/elapsed semantics, excluding cancelled and future
// sessions. A Consultation document is not evidence of a completed appointment.
export function completedSessionDate(row, now = Date.now()) {
  const status = String(row.appointment_status || '').toLowerCase();
  if (['cancelled', 'canceled'].includes(status)) return null;
  const end = timestamp(row.appointment_endtime);
  const date = timestamp(row.appointment_date);
  if (!Number.isFinite(date) || date > now || (Number.isFinite(end) && end > now)) return null;
  return status === 'completed' || (Number.isFinite(end) && end < now)
    ? new Date(date).toISOString() : null;
}

async function decodeIdentity(client, encrypted) {
  const { data, error } = await client.rpc('decrypt_text', { encrypted_data: encrypted });
  if (error) throw new Error('Session identity could not be verified.');
  const token = data?.result ?? data;
  if (typeof token !== 'string' || !token) throw new Error('Session identity could not be verified.');
  if (uuid.test(token)) return { kind: 'account', value: token };
  let phone;
  try { phone = await secureUnhash(token, process.env.REACT_APP_PATIENT_ID_SECRET); }
  catch {
    // Legacy plaintext phone values are accepted only as phones, never by
    // stripping arbitrary ciphertext down to its incidental numeric characters.
    if (/^\+?[\d ()-]+$/.test(token)) phone = token;
  }
  if (!phone || !/^\+?[\d ()-]+$/.test(phone)) throw new Error('Session identity could not be verified.');
  return { kind: 'phone', value: digits(phone) };
}

export async function loadClientTherapyHistory(client, { teamId, therapistId, patients, now = Date.now() }) {
  if (!teamId || !therapistId) throw new Error('Choose an organization before loading sessions.');
  const history = Object.fromEntries(patients.map(patient => [patient.id, []]));
  if (!patients.length) return history;
  const emails = new Map(), phones = new Map(), accounts = new Map();
  const add = (index, key, id) => {
    if (!key) return;
    if (!index.has(key)) index.set(key, new Set());
    index.get(key).add(id);
  };
  for (const patient of patients) {
    add(emails, emailKey(patient.email), patient.id);
    if (patient.phoneNumber) add(phones, digits(patient.phone_code) + digits(patient.phoneNumber), patient.id);
    add(accounts, patient.id, patient.id);
  }
  if (emails.size) {
    // Identity metadata only. Connection/consent continues to gate chat/vitals,
    // not the clinic's own appointment history. Emails stay in the POST body.
    const { data, error } = await client.rpc('get_scribe_connection_statuses', {
      p_therapist_user_id: therapistId, p_client_emails: [...emails.keys()],
    });
    if (error || !Array.isArray(data)) throw new Error('Session identities could not be loaded.');
    for (const row of data) {
      for (const patientId of emails.get(emailKey(row.client_email)) || []) {
        add(accounts, row.mindvault_account_id, patientId);
      }
    }
  }
  const decoded = new Map(), seen = new Set();
  // Explicit paging avoids Supabase's default row cap. Do not send a growing
  // patient-ID list in a URL or silently turn a truncated result into a count.
  let offset = 0, total = null;
  while (total === null || offset < total) {
    const { data, error, count } = await client.from('appointments')
      .select('id,appointment_date,appointment_endtime,appointment_status,patient_id,mindvault_client_email',
        total === null ? { count: 'exact' } : {})
      .eq('team_id', teamId).lte('appointment_date', new Date(now).toISOString())
      .order('id', { ascending: true }).range(offset, offset + 499);
    if (error || !Array.isArray(data)) throw new Error('Completed sessions could not be loaded.');
    if (total === null) {
      if (!Number.isInteger(count) || count < 0) throw new Error('Completed session totals could not be verified.');
      total = count;
    }
    if (!data.length && offset < total) throw new Error('Completed session history changed. Please retry.');
    for (const row of data) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      const date = completedSessionDate(row, now);
      if (!date) continue;
      let matches;
      if (row.mindvault_client_email != null) {
        // An empty/mismatching explicit link must never fall back to a phone.
        matches = emails.get(emailKey(row.mindvault_client_email));
      } else {
        if (!decoded.has(row.patient_id)) decoded.set(row.patient_id, decodeIdentity(client, row.patient_id));
        const identity = await decoded.get(row.patient_id);
        matches = (identity.kind === 'account' ? accounts : phones).get(identity.value);
      }
      if (!matches?.size) continue;
      if (matches.size !== 1) {
        for (const id of matches) history[id] = null; // ambiguous, not zero
        continue;
      }
      const [id] = matches;
      history[id]?.push(date);
    }
    // A project's row limit may be lower than the requested page size.
    offset += data.length;
  }
  if (seen.size !== total) throw new Error('Completed session history changed. Please retry.');
  for (const dates of Object.values(history)) dates?.sort();
  return history;
}

export function clientTherapyStats(history, start, end) {
  if (!history) return {};
  const from = timestamp(start), to = timestamp(end);
  return Object.fromEntries(Object.entries(history).map(([id, dates]) => [id, dates && {
    lastSessionDate: dates[dates.length - 1] || null,
    completedCount: dates.filter(date => timestamp(date) >= from && timestamp(date) <= to).length,
  }]));
}

export function clientAge(patient, now = new Date()) {
  const match = typeof patient.birth_date === 'string' && patient.birth_date.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
  if (match) {
    const [, y, m, d] = match.map(Number);
    const born = new Date(y, m - 1, d);
    if (born.getFullYear() === y && born.getMonth() === m - 1 && born.getDate() === d && born <= now) {
      const age = now.getFullYear() - y - (now.getMonth() < m - 1 || (now.getMonth() === m - 1 && now.getDate() < d) ? 1 : 0);
      if (age <= 130) return age;
    }
  }
  const age = typeof patient.age === 'number' || /^\d+$/.test(String(patient.age ?? '')) ? Number(patient.age) : NaN;
  return Number.isInteger(age) && age >= 0 && age <= 130 ? age : '—';
}
